import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  getMyShopProfile,
  getSellerOverview,
  phoneChangeSend,
  phoneChangeVerify,
  updateMyShopProfile,
  type MyShopProfile,
  type MyShopProfileUpdate,
  type SellerOverview,
} from "@/features/seller-portal/api";

export const sellerPortalKeys = {
  profile: ["seller", "profile"] as const,
  overview: ["seller", "overview"] as const,
};

export function useSellerProfile(): UseQueryResult<MyShopProfile, Error> {
  return useQuery({
    queryKey: sellerPortalKeys.profile,
    queryFn: getMyShopProfile,
  });
}

export function useSellerOverview(): UseQueryResult<SellerOverview, Error> {
  return useQuery({
    queryKey: sellerPortalKeys.overview,
    queryFn: getSellerOverview,
  });
}

export function useUpdateSellerProfile(): UseMutationResult<
  MyShopProfile,
  Error,
  MyShopProfileUpdate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MyShopProfileUpdate) => updateMyShopProfile(body),
    onSuccess: (data) => {
      qc.setQueryData(sellerPortalKeys.profile, data);
      void qc.invalidateQueries({ queryKey: sellerPortalKeys.overview });
    },
  });
}

export function usePhoneChangeSend(): UseMutationResult<
  { retry_after_seconds: number },
  Error,
  { new_phone_e164: string }
> {
  return useMutation({ mutationFn: phoneChangeSend });
}

export function usePhoneChangeVerify(): UseMutationResult<
  { phone_e164: string },
  Error,
  { otp: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: phoneChangeVerify,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: sellerPortalKeys.profile }); },
  });
}
